import type { PlanDetailsCopy } from '../planDetails'

const it: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'La tua età',
    retirement_age: 'Quando vai in pensione',
    annual_income: 'Reddito annuo',
    annual_savings: 'Risparmio annuo',
    inflation: 'Inflazione',
    realization_tax_rate: 'Aliquota sulle plusvalenze',
    cost_basis: 'Costo di acquisto',
    w0: 'Patrimonio investibile oggi',
    primary_residence: 'La tua abitazione',
    total_investable: 'Patrimonio investibile',
    expected_return: 'Rendimento atteso',
    volatility: 'Volatilità',
    horizon: 'Orizzonte di pianificazione',
    jurisdiction: 'Residenza fiscale',
    risk_aversion: 'Tolleranza al rischio',
    birth_year: 'Anno di nascita',
    birth_date: 'Data di nascita',
    marital_status: 'Stato civile',
    dependents: 'Familiari a carico',
    dependent_ages: 'Età dei familiari a carico',
    children: 'Figli',
    children_birth_years: 'Anni di nascita dei figli',
    partner_age: 'Età del partner',
    partner_annual_income: 'Reddito annuo del partner',
    partner_retirement_age: 'Età di pensionamento del partner',
    plan_to_age: 'Età di fine piano',
    gross_annual_income: 'Reddito annuo lordo',
    net_income: 'Reddito netto',
    annual_spending: 'Spesa annua',
    essential_spending: 'Spese essenziali',
    flexible_spending: 'Spese flessibili',
    investments_total: 'Totale investito',
    total_investable_wealth: 'Patrimonio investibile totale',
    portfolio_mix: 'Allocazione del portafoglio',
    cgt_rate: 'Aliquota sui redditi di capitale',
    interest_rate: 'Tasso di interesse',
    dividend_yield: 'Rendimento da dividendi',
    wealth_tax_rate: 'Aliquota dell’imposta patrimoniale',
    filing_jurisdictions: 'Paesi in cui presenti la dichiarazione',
    citizenship: 'Cittadinanza',
    tax_residency: 'Residenza fiscale dichiarata',
    state: 'Stato o provincia',
    region: 'Regione',
    city: 'Città',
    locality: 'Comune',
    church_tax_applicable: 'Soggetto all’imposta ecclesiastica',
    life_cover: 'Assicurazione caso morte',
    income_protection: 'Tutela del reddito',
    employer_match: 'Contributo del datore di lavoro',
    pension: 'Pensione',
    state_pension: 'Pensione pubblica',
    social_security: 'Previdenza sociale',
    wage_growth: 'Crescita salariale',
    stability: 'Stabilità del reddito',
    has_will: 'Testamento redatto',
    risk_attitude: 'Atteggiamento verso il rischio',
  },
  keyLabels: {
    goal_priority: 'Ordine degli obiettivi',
    current_age: 'Età',
    inflation: 'Ipotesi di inflazione',
    jurisdiction: 'Residenza fiscale',
    realization_tax_rate: 'Imposta sulla vendita per finanziare un obiettivo',
    citizenship: 'Cittadinanza',
    total_investable: 'Patrimonio investibile',
    annual_income: 'Reddito annuo',
    annual_savings: 'Risparmio annuo',
    risk_tier: 'Livello di rischio',
    retirement_age: 'Età di pensionamento',
    target_wealth: 'Patrimonio obiettivo',
    primary_residence_sellable: 'Vendita della casa / trasferimento in una più piccola',
  },
  channelLabels: {
    web_levers: 'Leve sul sito',
    web_cockpit: 'Cruscotto decisionale',
    web: 'Sito web',
    chat: 'Assistente IA',
    mcp: 'Assistente IA',
    full_review: 'Revisione completa',
    full_plan: 'Piano completo',
    what_if: 'Simulazione di scenari',
  },
  objectiveLabels: {
    status_quo: 'Situazione attuale',
    max_goal_probability: 'Massima probabilità di successo',
    lowest_drawdown: 'Minore perdita massima',
    highest_growth: 'Massima crescita',
    fastest_to_goal: 'Obiettivo raggiunto prima',
  },
  choices: {
    interest_only: 'Solo interessi',
    fixed_rate_amortizing: 'Ammortamento (tasso fisso)',
    variable_rate_amortizing: 'Ammortamento (tasso variabile)',
    repayment: 'Ammortamento',
    refinance: 'Rifinanziare al tasso di mercato',
    repay: 'Rimborsare integralmente',
  },
  values: {
    yearsShort: (n) => `${n} anni`,
    shareAbout: (pct) => `circa il ${pct} del suo valore`,
    sharePart: 'parte del suo valore',
    aroundAge: (age) => ` intorno ai ${age} anni`,
    inAboutYears: (n) => ` tra circa ${n} anni`,
    maySellFor: (share, when) => `Può essere venduto per liberare ${share}${when}, solo se ciò migliora il piano.`,
    maySell: 'Può essere venduto se ciò migliora il piano.',
    structuredFact: 'Dato strutturato',
  },
  ask: { talkThrough: 'Parlane con MyCIO' },
  review: {
    openInputCheckFailed: 'Non è stato possibile completare la verifica dei dati mancanti. Vedi lo stato della revisione qui sotto.',
    finishedLoading: 'La tua revisione è terminata. Caricamento dei risultati…',
    draftReady: 'La tua revisione provvisoria è pronta: qui sotto trovi l’analisi completata, ciò che non è stato possibile calcolare e le domande che permetterebbero di completarla.',
    finishedNotLoaded: 'La tua revisione è terminata, ma non è stato ancora possibile caricarne i risultati. Nuovo tentativo in corso: il calcolo non verrà ripetuto.',
    fetchFailed: 'La tua revisione è terminata, ma al momento non siamo riusciti a recuperarla. È salvata: riprova tra poco; non serve ricalcolarla.',
    notComputed: (list) => `Non calcolato in questa esecuzione: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 modifica non è stata salvata' : `${f} modifiche non sono state salvate`}: vedi Le tue informazioni.`,
  },
  facts: {
    chooseOption: 'Scegli un’opzione.',
    couldNotSave: 'Non è stato possibile salvare il valore. Il tuo piano non è cambiato.',
    couldNotUndo: 'Non è stato possibile annullare la modifica.',
    choosePlaceholder: 'Scegli…',
    confirmedBadge: 'Confermato: chiedi la modifica in chat',
    confirmedTitle: (was) => `Hai già confermato questo dato come ${was}. Chiedi a MyCIO in chat di sostituirlo.`,
  },
  assumptionsToConfirm: { anInput: 'un dato', assumedSuffix: (v) => ` — ipotesi: ${v}` },
  editor: {
    enterNumber: 'Inserisci un numero.',
    enterNumberFor: (l) => `Inserisci un numero per ${l}.`,
    enterPercentFor: (l) => `Inserisci una percentuale per ${l}.`,
    chooseYesNoFor: (l) => `Scegli Sì o No per ${l}.`,
    semicolonBetween: (l) => `Separa con ";" le voci di ${l}.`,
    useNumbersFor: (l) => `Usa numeri per ${l}.`,
    wholeNumbersFor: (l) => `Usa numeri interi per ${l}, separati da ";".`,
    commaSeparated: 'separati da virgole',
  },
  freshnessUnknown: 'Non siamo riusciti a verificare se il calcolo salvato include le ultime modifiche ai tuoi conti. Aggiorna la pagina e controlla i conti prima di basarti su queste cifre.',
  goals: { goalN: (n) => `Obiettivo ${n}`, whatMoves: 'cosa lo sposta →', earliest: (y) => `al più presto ≈ ${y}`, rank: 'Priorità' },
  issues: {
    mainConstraint: 'Il vincolo principale di questo piano',
    riskToReview: 'Rischio da esaminare',
    missingInformation: 'Informazioni mancanti',
    whyItMatters: (t) => `Perché conta: ${t}`,
    seeEvidence: 'Vedi gli elementi a supporto ↓',
    noneFound: 'L’analisi completata non ha individuato criticità rilevanti con le informazioni attualmente presenti in questo piano.',
    notAvailable: 'L’analisi delle criticità non è ancora disponibile. Una sezione vuota non significa che non ci siano criticità.',
    discussQuestion: 'Esamina le criticità individuate da questo piano. Spiega su cosa si basano e chiedimi se qualche informazione mancante sulla mia situazione potrebbe cambiarne la priorità o la conclusione.',
    discussLabel: 'Discuti queste criticità',
  },
  policies: {
    optionTested: 'Opzione analizzata',
    computedEffect: (v) => `Effetto calcolato: ${v}`,
    tradeoff: (v) => `Compromesso: ${v}`,
    backToAction: 'Torna all’azione ↑',
    tradeoffsHeading: 'Compromessi dai casi analizzati',
  },
  transfer: {
    toHeirs: (m) => `+${m} agli eredi`,
    fundedPoints: (label, pts) => `${label} ${pts} punti percentuali`,
    noChange: ' · nessuna variazione nel finanziamento dei tuoi obiettivi',
  },
  tax: {
    rulesIntro: 'Sono mostrate solo le norme collegate a questo calcolo. Ogni voce riporta i dati della fonte così come sono stati registrati; le date o le informazioni sull’ambito di applicazione mancanti restano indicate come mancanti.',
    title: 'Trattamento fiscale usato in questo piano',
    countryBookPrimary: 'La proiezione ha applicato anno per anno la normativa fiscale del paese interessato. Le fonti registrate espressamente come ufficiali o primarie sono elencate qui sotto.',
    countryBookNoPrimary: 'La proiezione ha applicato anno per anno la normativa fiscale del paese interessato. A questo risultato salvato non sono ancora collegate fonti primarie: verifica le decisioni con rilevanza fiscale prima di agire.',
    simplified: 'Il calcolo ha usato un trattamento fiscale semplificato. Esamina i limiti qui sotto prima di basarti su confronti con rilevanza fiscale.',
    askQuestion: 'Spiega quali leggi fiscali e quali livelli nazionali sono stati applicati a questo piano, quali sono stati approssimati o esclusi e quali elementi della mia situazione fiscale potrebbero cambiare il risultato. Usa solo le fonti primarie effettivamente collegate a questo calcolo e indica quelle mancanti.',
    askLabel: 'Chiedi del trattamento fiscale',
  },
  inputs: { title: 'Dati che potrebbero cambiare il risultato', infoToConfirm: 'Informazioni da confermare' },
  gated: {
    countChoices: (n, f) => (n === 1 ? `${f} opzione elaborata per te` : `${f} opzioni elaborate per te`),
    mappedYears: (_n, f) => `abbiamo proiettato i prossimi ${f} anni`,
    extraChecks: (n, f) => (n === 1 ? `${f} verifica aggiuntiva del tuo piano` : `${f} verifiche aggiuntive del tuo piano`),
    placeholderRows: ['Riga illustrativa', 'Cifra di esempio', 'Opzione di esempio', 'Segnaposto'],
  },
}

export default it
